import React, { useEffect, useState } from "react";
import { formatPrice } from "../lib/format";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../contexts/CartContext";
import { api } from "../lib/api";
import { X, Minus, Plus, AlertTriangle } from "lucide-react";

const REASON_FR = {
  out_of_stock: "épuisé",
  insufficient_stock: "stock insuffisant",
  inactive: "indisponible",
  not_found: "introuvable",
};

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, subtotal, shipping, total, freeShippingThreshold, shippingPromo } = useCart();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);

  // Re-check stock against the live catalogue each time the drawer opens.
  // Catches the "cart sat for hours / other customer bought the last one"
  // case before the customer wastes time on the checkout form.
  useEffect(() => {
    if (!open || items.length === 0) {
      setIssues([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/cart/validate", items.map(i => ({
          product_id: i.product_id, size: i.size, quantity: i.quantity,
        })));
        if (!cancelled) setIssues(r.data.issues || []);
      } catch {
        // Validation is best-effort — a network blip shouldn't block the
        // drawer. The server-side check at /orders still catches it.
        if (!cancelled) setIssues([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, items]);

  const hasBlocking = issues.length > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md bg-berulia-beige border-l border-berulia-border p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-berulia-border">
          <SheetTitle className="font-heading text-2xl">Votre panier</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {items.length === 0 && (
            <p className="text-center text-berulia-inkmuted py-12">Votre panier est vide. <br/><Link to="/boutique" className="link-underline overline mt-3 inline-block" onClick={()=>setOpen(false)}>Découvrir la boutique</Link></p>
          )}
          {hasBlocking && (
            <div className="border border-red-200 bg-red-50 text-red-900 p-3 text-sm rounded" data-testid="cart-issues">
              <div className="flex items-center gap-2 font-medium mb-2">
                <AlertTriangle size={14}/> Certains articles ne sont plus disponibles
              </div>
              <ul className="space-y-1 list-disc list-inside">
                {issues.map((iss, idx) => {
                  const reason = REASON_FR[iss.reason] || iss.reason;
                  if (iss.reason === "insufficient_stock") {
                    return <li key={idx}>{iss.name} (taille {iss.size}) — reste {iss.available}, vous en demandez {iss.requested}</li>;
                  }
                  return <li key={idx}>{iss.name} (taille {iss.size}) — {reason}</li>;
                })}
              </ul>
              <p className="text-xs mt-2 italic">Ajustez la quantité ou retirez l'article pour continuer.</p>
            </div>
          )}
          {items.map(it => {
            const issue = issues.find(i => i.product_id === it.product_id && i.size === it.size);
            return (
              <div key={`${it.product_id}-${it.size}`} className={`flex gap-4 ${issue ? "opacity-60" : ""}`} data-testid={`cart-item-${it.product_id}`}>
                <img src={it.image} alt={it.name} className="w-20 h-28 object-cover bg-white"/>
                <div className="flex-1">
                  <p className="font-heading text-lg leading-tight">{it.name}</p>
                  <p className="text-xs text-berulia-inkmuted mt-1">Taille {it.size}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => updateQty(it.product_id, it.size, it.quantity - 1)} className="w-7 h-7 border border-berulia-border flex items-center justify-center"><Minus size={12}/></button>
                    <span className="w-6 text-center text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.size, it.quantity + 1)} className="w-7 h-7 border border-berulia-border flex items-center justify-center"><Plus size={12}/></button>
                  </div>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <button onClick={() => remove(it.product_id, it.size)} className="text-berulia-inkmuted hover:text-berulia-ink" data-testid={`remove-${it.product_id}`}><X size={14}/></button>
                  <p className="text-sm">{formatPrice((it.unit_price * it.quantity))}</p>
                </div>
              </div>
            );
          })}
        </div>
        {items.length > 0 && (
          <div className="border-t border-berulia-border px-6 py-5 space-y-3 bg-white">
            {/* Shipping progress */}
            {subtotal < freeShippingThreshold ? (
              <div>
                <p className="text-xs text-berulia-inkmuted">Plus que <strong>{formatPrice((freeShippingThreshold - subtotal))}</strong> pour la livraison offerte ✿</p>
                <div className="h-1 bg-berulia-beigewarm mt-2 rounded-full overflow-hidden">
                  <div className="h-full bg-berulia-rosedeep transition-all" style={{width: `${Math.min(100, (subtotal/freeShippingThreshold)*100)}%`}}/>
                </div>
              </div>
            ) : (
              <p className="text-xs text-berulia-rosedeep font-medium">
                {shippingPromo ? "✿ Livraison offerte sur toute la commande, tout le week-end" : "✿ Votre commande bénéficie de la livraison offerte"}
              </p>
            )}
            <div className="flex justify-between text-sm"><span>Sous-total</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span>Livraison</span><span>{shipping === 0 ? "Offerte" : formatPrice(shipping)}</span></div>
            <div className="flex justify-between font-heading text-xl pt-2 border-t border-berulia-border"><span>Total</span><span>{formatPrice(total)}</span></div>
            <p className="text-[11px] text-berulia-inkmuted italic text-center">Frais de port et remises calculés à la caisse</p>
            <button onClick={() => { setOpen(false); navigate("/checkout"); }}
              disabled={hasBlocking}
              data-testid="checkout-btn"
              className="w-full bg-berulia-rosedeep text-berulia-inkdeep overline py-4 hover:bg-berulia-rose transition disabled:opacity-50 disabled:cursor-not-allowed">
              {hasBlocking ? "Ajustez le panier" : "Passer commande →"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
